import '../styles/ExtractionRunPanel.css';
import React, { useId, useState } from 'react';
import { useProjectCollection, projectData } from '../lib/projectData';

const STATE_LABELS = {
  idle: 'Idle — no run started',
  running: 'Running',
  complete: 'Complete',
  failed: 'Failed',
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function ExtractionRunPanel({ onRunExtraction }) {
  const [inputValue, setInputValue] = useState('');
  const [runStatus, setRunStatus] = useState('idle');
  const [runError, setRunError] = useState('');
  const [inputError, setInputError] = useState('');
  const { data: runs, loading, error, refresh } = useProjectCollection('extraction_runs');
  const inputId = useId();
  const hintId = useId();

  const running = runStatus === 'running';

  const recentRuns = (Array.isArray(runs) ? runs : [])
    .slice()
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .slice(0, 2);

  async function execute() {
    const title = inputValue.trim();
    if (!title) {
      setInputError('Enter the LinkedIn profile, company or search you want to extract from.');
      return;
    }
    setInputError('');
    setRunError('');
    if (typeof onRunExtraction !== 'function') {
      setRunError('Extraction is not available in this preview. The run was not started and nothing was saved.');
      setRunStatus('failed');
      return;
    }
    setRunStatus('running');
    try {
      const result = await onRunExtraction({ query: title });
      if (!result || result.ok !== true) {
        setRunError(
          (result && typeof result.error === 'string' && result.error) ||
            'The extraction run did not complete. The reason was not returned.',
        );
        setRunStatus('failed');
        return;
      }
      const record = {
        id: `run-${Date.now().toString(36)}`,
        title,
        createdAt: today(),
        status: 'complete',
        resultTotal: Number(result.resultTotal) || 0,
      };
      await projectData.create('extraction_runs', record);
      setRunStatus('complete');
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('run', record.id);
        url.searchParams.set('title', title);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (err) {
        // URL sync is best effort; the event below still delivers the run.
      }
      window.dispatchEvent(
        new CustomEvent('extraction:run-complete', { detail: { run: record.id, title } }),
      );
      await refresh();
    } catch (err) {
      setRunError((err && err.message) || 'The extraction run could not be completed.');
      setRunStatus('failed');
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (running) return;
    execute();
  }

  function handleRetry() {
    if (running) return;
    execute();
  }

  const lastRun = recentRuns[0];

  return (
    <section className="erp-root" aria-labelledby={`${inputId}-panel-title`} aria-busy={running}>
      <div className="erp-panel">
        <header className="erp-head">
          <div className="erp-head-title">
            <span className="erp-index" aria-hidden="true">01</span>
            <h2 className="erp-title" id={`${inputId}-panel-title`}>Run an extraction</h2>
          </div>
          <p className="erp-state" data-status={runStatus} role="status" aria-live="polite">
            <span className="erp-state-dot" aria-hidden="true" />
            <span>{STATE_LABELS[runStatus]}</span>
          </p>
        </header>

        <form className="erp-form" onSubmit={handleSubmit} noValidate>
          <div className="erp-field">
            <label className="erp-label erp-field-label" htmlFor={inputId}>Extraction input</label>
            <input
              id={inputId}
              className="erp-input"
              type="text"
              name="extractionInput"
              value={inputValue}
              onChange={(event) => {
                setInputValue(event.target.value);
                if (inputError) setInputError('');
              }}
              placeholder="e.g. Product leaders in Berlin"
              autoComplete="off"
              disabled={running}
              aria-invalid={inputError ? 'true' : 'false'}
              aria-describedby={hintId}
              maxLength={160}
            />
            <p className="erp-hint" id={hintId} data-tone={inputError ? 'invalid' : undefined}>
              {inputError || (running ? 'The instrument is working. The run control is locked until it finishes.' : 'Name the LinkedIn profile, company or search to extract leads from.')}
            </p>
          </div>

          <div className="erp-controls">
            <button type="submit" className="erp-run" disabled={running} data-live={running ? 'true' : 'false'}>
              <span>{running ? 'Running…' : runStatus === 'failed' ? 'Run again' : 'Start extraction'}</span>
              <span className="erp-run-mark" aria-hidden="true" />
            </button>
          </div>
        </form>

        {runStatus === 'failed' && (
          <div className="erp-failure" role="alert">
            <div className="erp-failure-body">
              <p className="erp-label">Run failed</p>
              <h3 className="erp-failure-title">The extraction did not complete</h3>
              <p className="erp-failure-reason">{runError}</p>
            </div>
            <button type="button" className="erp-retry" onClick={handleRetry} disabled={running}>
              <span>Retry run</span>
              <span aria-hidden="true">↻</span>
            </button>
          </div>
        )}

        <div className="erp-foot">
          <div className="erp-foot-head">
            <p className="erp-label">Previous runs</p>
            {loading && <span className="erp-foot-status">Loading…</span>}
            {!loading && error && (
              <button type="button" className="erp-foot-link" onClick={refresh}>
                Runs unavailable — retry
              </button>
            )}
            {!loading && !error && !recentRuns.length && (
              <span className="erp-foot-status">No runs recorded</span>
            )}
          </div>
          {!loading && !error && lastRun && (
            <ul className="erp-runs">
              {recentRuns.map((run) => (
                <li className="erp-run-row" key={run.id}>
                  <span className="erp-run-name">{run.title}</span>
                  <span className="erp-run-meta">
                    {run.createdAt} · <span className="erp-run-total">{run.resultTotal}</span> leads
                  </span>
                </li>
              ))}
            </ul>
          )}
          <a className="erp-foot-link" href="/leads">Open saved lead lists</a>
        </div>
      </div>
    </section>
  );
}

export default ExtractionRunPanel;